import { flexRender, Table } from '@tanstack/react-table';
import { FC, Fragment, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { useTableSpacerWidth } from '@/hooks';
import { ActiveUserData } from '@/api';

export interface UploadedDocumentsTableProps {
  table: Table<ActiveUserData>;
}

export const ActiveUsersListTable: FC<UploadedDocumentsTableProps> = ({ table }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const rows = table.getRowModel().rows;

  useTableSpacerWidth(containerRef, tableRef, [rows]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <table
        ref={tableRef}
        className={classNames('min-w-full border-none', {
          'h-full': rows.length === 0,
        })}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={classNames('sticky top-0 z-10 h-12 max-h-12 min-h-12')}>
              {headerGroup.headers.map((header) => {
                return (
                  <Fragment key={header.id}>
                    <th className="bg-fletchers-white px-2 py-1" style={{ boxShadow: 'inset 0 -1px 0 #E1E1E0' }}>
                      <div className="flex items-center justify-start">
                        <div
                          className={classNames(
                            'flex items-center justify-center',
                            'text-fletchers-gray-175 text-[14px]/[20px] font-[600]',
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      </div>
                    </th>
                    {header.id !== 'actions' && (
                      <th
                        aria-hidden
                        data-spacer
                        className="bg-fletchers-white"
                        style={{ width: 'var(--spacer-w, 100%)', boxShadow: 'inset 0 -1px 0 #E1E1E0' }}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  className={classNames(
                    'group h-12 max-h-12 min-h-12',
                    'bg-fletchers-white border-fletchers-gray-110 border-b last:border-b-0',
                    'hover:bg-fletchers-gray-50',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Fragment key={cell.id}>
                      <td className={classNames('border-none px-2 py-1', 'text-[14px]/[20px] font-[400]')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext()) as React.ReactNode}
                      </td>
                      {cell.column.id !== 'actions' && (
                        <td
                          aria-hidden
                          data-spacer
                          className="border-none"
                          style={{ width: 'var(--spacer-w, 100%)' }}
                        />
                      )}
                    </Fragment>
                  ))}
                </tr>
              </Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={table.getAllColumns().length * 2}>
                <div
                  className={classNames(
                    'flex items-center justify-center',
                    'border-none px-2 py-1',
                    'text-[14px]/[20px] font-[400]',
                  )}
                >
                  No results found
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
